// The WEB half of the passkey seam (@merkury/brain identity/passkeyProvider.ts):
// BrainAccount runs one ceremony implementation everywhere and this adapts it to
// `navigator.credentials` — and, inside the Capacitor WebView, to the native
// Credential Manager plugin, since that WebView has no WebAuthn of its own
// (webauthn.ts owns that branch; B3-android replaces it with the brain's native
// provider).
//
// Two things this file exists to do, beyond plumbing:
//
// 1. ENCODING. The brain deals in raw PRF bytes; the browser wants
//    `BufferSource` salts and hands results back as ArrayBuffers. webauthn.ts
//    already does that conversion, so this is a re-wrap, not a reimplementation.
//
// 2. ERROR CLASSIFICATION. WebAuthn rejects with DOMExceptions whose `name` is
//    overloaded — `NotAllowedError` means "user dismissed" AND "this browser
//    lacks Related Origin Requests and the page is on a mirror origin". Only the
//    UI can tell those apart usefully, and only from the rpId the SERVER pinned
//    into the options, so the classification happens here (where both are in
//    hand) and rides out on `.code` in the brain's stable vocabulary.

import type { WebAuthnJson } from '@merkury/protocol';
import {
  withPasskeyCode,
  type PasskeyCeremonyRequest,
  type PasskeyCeremonyResult,
  type PasskeyProvider,
} from '@merkury/brain/identity/passkeyProvider.js';
import {
  classifyWebAuthnError,
  createCredential,
  createCredentialWithPrf,
  getAssertion,
  getAssertionWithPrf,
  rpIdFromOptions,
  supportsWebAuthn,
  type PrfCeremony,
  type PrfSalts,
} from './webauthn.js';

/** `WebAuthnFailure` → the brain's `PasskeyErrorCode`. */
const FAILURE_CODES: Record<string, string> = {
  cancelled: 'cancelled',
  unsupported: 'unsupported',
  wrongOrigin: 'wrong-origin',
  unknown: 'error',
};

function toResult(ceremony: PrfCeremony): PasskeyCeremonyResult {
  return {
    credential: ceremony.credential,
    prfEnabled: ceremony.prfEnabled,
    ...(ceremony.prfOutput ? { prfOutput: ceremony.prfOutput } : {}),
    ...(ceremony.prfOutputSecond ? { prfOutputSecond: ceremony.prfOutputSecond } : {}),
  };
}

function saltsFrom(req: PasskeyCeremonyRequest): PrfSalts | undefined {
  if (!req.prf) return undefined;
  return { first: req.prf.first, ...(req.prf.second ? { second: req.prf.second } : {}) };
}

/**
 * Run a ceremony, stamping a stable `.code` on any rejection. The ROR check
 * needs the server-pinned rpId, so it is read from the options here rather than
 * guessed downstream.
 *
 * ACTIVATION-SAFETY: no `await` may be introduced before `run()` — Safari only
 * honours the ceremony inside the click's transient activation window, and the
 * options are passed in already fetched for exactly that reason (BrainAccount
 * prefetches them). Guarded by the "no async gap" tests.
 */
async function guarded(
  options: WebAuthnJson,
  run: () => Promise<PrfCeremony | undefined>,
): Promise<PasskeyCeremonyResult | undefined> {
  try {
    const ceremony = await run();
    return ceremony ? toResult(ceremony) : undefined;
  } catch (err) {
    const rpId = rpIdFromOptions(options);
    const failure = classifyWebAuthnError(err, rpId);
    const coded = withPasskeyCode(err, FAILURE_CODES[failure] ?? 'error') as Error & {
      rpId?: string;
    };
    // The UI names the primary domain in the wrong-origin copy, and by the time
    // the error surfaces the options are long gone — carry it on the error.
    if (rpId !== undefined) coded.rpId = rpId;
    throw coded;
  }
}

/** A plain (no-PRF) ceremony result, for the callers that ask for no salts. */
function plain(credential: WebAuthnJson | undefined): PrfCeremony | undefined {
  return credential ? { credential, prfEnabled: false } : undefined;
}

export const webPasskeyProvider: PasskeyProvider = {
  supported: supportsWebAuthn,
  create(req: PasskeyCeremonyRequest): Promise<PasskeyCeremonyResult | undefined> {
    const salts = saltsFrom(req);
    return guarded(req.options, () =>
      salts
        ? createCredentialWithPrf(req.options, salts)
        : createCredential(req.options).then(plain),
    );
  },
  assert(req: PasskeyCeremonyRequest): Promise<PasskeyCeremonyResult | undefined> {
    const salts = saltsFrom(req);
    return guarded(req.options, () =>
      salts ? getAssertionWithPrf(req.options, salts) : getAssertion(req.options).then(plain),
    );
  },
};
